import { useEffect, useMemo } from 'react'

import { useLazyQuery, useQuery } from '@apollo/client/react'
import { Temporal } from '@js-temporal/polyfill'

import { useCalendar } from '~publish/components/Calendar/hooks/useCalendar'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql, readFragment } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import { buildPreviewCalendarSlots } from '~publish/helpers/buildPreviewCalendarSlots'
import { endOfDay, startOfWeek } from '~publish/helpers/temporal'
import { useSlotsEnabled } from '~publish/hooks/useSlotsEnabled'
import { useTimezone } from '~publish/hooks/useTimezone'
import { useWeekStartsOn } from '~publish/hooks/useWeekStartsOn'
import { GetChannelInfo } from '~publish/pages/Channel'
import { QueueList_Channel } from '~publish/pages/Channel/QueueList/fragments/QueueList_Channel'
import { generatePostingSlotsUntilDate } from '~publish/pages/Channel/QueueList/postSlotManager'
import type {
  ScheduleV2,
  Slot,
} from '~publish/pages/Channel/QueueList/postSlotManager'

const MAX_MULTI_CHANNEL_SLOTS_PER_CHANNEL_PER_WEEK = 3
const DEFAULT_MULTI_CHANNEL_SLOTS_PER_WEEK = 3

const GetCalendarPostingGoals = graphql(/* GraphQL */ `
  query GetCalendarPostingGoals($organizationId: OrganizationId!) {
    organizationPostingGoalsSummary(
      input: { organizationId: $organizationId }
    ) {
      channelGoals {
        channel {
          id
        }
        postingGoal {
          goal
        }
      }
    }
  }
`)

export { buildPreviewCalendarSlots }

type QueueListChannel = ResultOf<typeof QueueList_Channel>
type Service = QueueListChannel['service']

export type CalendarSlotChannel = {
  id: string
  name: string
  service: QueueListChannel['service']
  timezone: string
  isQueuePaused: boolean
  postingSchedule: ScheduleV2[]
  postingGoal?: QueueListChannel['postingGoal']
}

export type CalendarSlot = Slot & {
  id: string
  channelId: string | null
  channelName: string
  channelService: QueueListChannel['service']
}

export function canShowMultiChannelCalendarSlots({
  channelsLoading,
  viewOnly,
}: {
  channelsLoading: boolean
  viewOnly: boolean
}): boolean {
  return !channelsLoading && !viewOnly
}

export function areCalendarSlotsEnabled({
  isSingleChannel,
  showTimeSlots,
  showMultiChannelSlots,
}: {
  isSingleChannel: boolean
  showTimeSlots: boolean
  showMultiChannelSlots: boolean
}): boolean {
  return showTimeSlots && (isSingleChannel || showMultiChannelSlots)
}

function buildSlotsForChannel({
  channel,
  startTimestamp,
  endDate,
}: {
  channel: CalendarSlotChannel
  startTimestamp?: number
  endDate: string
}): CalendarSlot[] {
  if (channel.isQueuePaused) return []

  const postingSchedule = channel.postingSchedule.filter(
    (schedule) => !schedule.paused,
  )
  if (postingSchedule.length === 0) return []

  const slots = generatePostingSlotsUntilDate({
    postingSchedule,
    timezone: channel.timezone,
    endDate,
  })
    .filter(
      (slot) =>
        startTimestamp === undefined ||
        new Date(slot.date).getTime() >= startTimestamp,
    )
    .map((slot) => ({
      date: slot.date,
      id: `slot:${channel.id}:${slot.date}`,
      channelId: channel.id,
      channelName: channel.name,
      channelService: channel.service,
    }))

  return slots
}

function getWeekKey(
  date: string,
  timezone: string,
  weekStartsOn: 0 | 1,
): string {
  const zonedDate = Temporal.Instant.from(date).toZonedDateTimeISO(timezone)
  return startOfWeek(zonedDate, { weekStartsOn }).toPlainDate().toString()
}

function getDistributedIndexes(length: number, count: number): number[] {
  const selectedCount = Math.min(length, count)
  if (selectedCount <= 0) return []

  return Array.from({ length: selectedCount }, (_, index) =>
    Math.floor(((index + 0.5) * length) / selectedCount),
  )
}

function getSlotDay(slot: CalendarSlot, timezone: string): string {
  return Temporal.Instant.from(slot.date)
    .toZonedDateTimeISO(timezone)
    .toPlainDate()
    .toString()
}

function takeDistributedSlots({
  slots,
  count,
  timezone,
  selectedSlots,
}: {
  slots: CalendarSlot[]
  count: number
  timezone: string
  selectedSlots: CalendarSlot[]
}): CalendarSlot[] {
  if (count <= 0) return []

  const selectedIds = new Set(selectedSlots.map((slot) => slot.id))
  const selectedDays = new Set(
    selectedSlots.map((slot) => getSlotDay(slot, timezone)),
  )
  const candidates = slots
    .filter((slot) => !selectedIds.has(slot.id))
    .toSorted((a, b) => a.date.localeCompare(b.date))
  const slotsByUnusedDay = new Map<string, CalendarSlot[]>()

  for (const slot of candidates) {
    const day = getSlotDay(slot, timezone)
    if (selectedDays.has(day)) continue
    const daySlots = slotsByUnusedDay.get(day) ?? []
    daySlots.push(slot)
    slotsByUnusedDay.set(day, daySlots)
  }

  const unusedDays = [...slotsByUnusedDay.values()]
  const selected = getDistributedIndexes(unusedDays.length, count).map(
    (index) => unusedDays[index][0],
  )
  const remainingCount = count - selected.length
  if (remainingCount <= 0) return selected

  const usedIds = new Set([...selectedIds, ...selected.map((slot) => slot.id)])
  const selectedCountByDay = new Map<string, number>()
  for (const slot of [...selectedSlots, ...selected]) {
    const day = getSlotDay(slot, timezone)
    selectedCountByDay.set(day, (selectedCountByDay.get(day) ?? 0) + 1)
  }

  const remainingSlotsByDay = new Map<string, CalendarSlot[]>()
  for (const slot of candidates.filter(
    (candidate) => !usedIds.has(candidate.id),
  )) {
    const day = getSlotDay(slot, timezone)
    const daySlots = remainingSlotsByDay.get(day) ?? []
    daySlots.push(slot)
    remainingSlotsByDay.set(day, daySlots)
  }

  const fallbackSlots: CalendarSlot[] = []
  while (fallbackSlots.length < remainingCount) {
    const availableDays = [...remainingSlotsByDay.entries()].filter(
      ([, daySlots]) => daySlots.length > 0,
    )
    if (availableDays.length === 0) break

    const minimumSelectedCount = Math.min(
      ...availableDays.map(([day]) => selectedCountByDay.get(day) ?? 0),
    )
    const leastUsedDays = availableDays.filter(
      ([day]) => (selectedCountByDay.get(day) ?? 0) === minimumSelectedCount,
    )
    const slotsNeeded = remainingCount - fallbackSlots.length

    for (const index of getDistributedIndexes(
      leastUsedDays.length,
      slotsNeeded,
    )) {
      const [day, daySlots] = leastUsedDays[index]
      const slot = daySlots.shift()
      if (!slot) continue
      fallbackSlots.push(slot)
      selectedCountByDay.set(day, minimumSelectedCount + 1)
    }
  }

  return [...selected, ...fallbackSlots]
}

function selectWeeklySlots({
  slotsByChannel,
  timezone,
}: {
  slotsByChannel: { channel: CalendarSlotChannel; slots: CalendarSlot[] }[]
  timezone: string
}): CalendarSlot[] {
  const selectedSlots: CalendarSlot[] = []

  for (const { channel, slots } of slotsByChannel) {
    selectedSlots.push(
      ...takeDistributedSlots({
        slots,
        count: getChannelSlotLimit(channel),
        timezone,
        selectedSlots,
      }),
    )
  }

  return selectedSlots.toSorted((a, b) => a.date.localeCompare(b.date))
}

function getChannelSlotLimit(channel: CalendarSlotChannel): number {
  return Math.min(
    MAX_MULTI_CHANNEL_SLOTS_PER_CHANNEL_PER_WEEK,
    channel.postingGoal?.goal ?? DEFAULT_MULTI_CHANNEL_SLOTS_PER_WEEK,
  )
}

export function buildMultiChannelCalendarSlots({
  channels,
  timezone,
  weekStartsOn,
  repeatEachWeek = true,
  startTimestamp,
  endDate,
}: {
  channels: CalendarSlotChannel[]
  timezone: string
  weekStartsOn: 0 | 1
  repeatEachWeek?: boolean
  startTimestamp: number
  endDate: string
}): CalendarSlot[] {
  if (channels.length === 0) return []

  const slotsByChannel = channels.map((channel) => ({
    channel,
    slots: buildSlotsForChannel({
      channel,
      startTimestamp,
      endDate,
    }),
  }))

  if (!repeatEachWeek) {
    return selectWeeklySlots({
      slotsByChannel,
      timezone,
    })
  }

  const slotsByWeek = new Map<string, Map<string, CalendarSlot[]>>()
  for (const { channel, slots } of slotsByChannel) {
    for (const slot of slots) {
      const weekKey = getWeekKey(slot.date, timezone, weekStartsOn)
      const weekSlots = slotsByWeek.get(weekKey) ?? new Map()
      const channelSlots = weekSlots.get(channel.id) ?? []
      channelSlots.push(slot)
      weekSlots.set(channel.id, channelSlots)
      slotsByWeek.set(weekKey, weekSlots)
    }
  }

  return [...slotsByWeek.entries()]
    .toSorted(([firstWeek], [secondWeek]) =>
      firstWeek.localeCompare(secondWeek),
    )
    .flatMap(([, weekSlots]) => {
      const weeklySlotsByChannel = slotsByChannel.map(({ channel }) => ({
        channel,
        slots: weekSlots.get(channel.id) ?? [],
      }))

      return selectWeeklySlots({
        slotsByChannel: weeklySlotsByChannel,
        timezone,
      })
    })
}

export function useCalendarSlots({
  selectedChannelIds,
  channels = [],
  onboardingServices = null,
  enableMultiChannelSlots = false,
}: {
  selectedChannelIds?: string[] | void
  channels?: CalendarSlotChannel[]
  onboardingServices?: Service[] | null
  enableMultiChannelSlots?: boolean
}): CalendarSlot[] {
  const [showTimeSlots] = useSlotsEnabled()
  const timezone = useTimezone()
  const dayToStartWeek = useWeekStartsOn()
  const weekStartsOn = dayToStartWeek === 'monday' ? 1 : 0
  const { startDate, endDate, viewMode } = useCalendar({
    timezone,
    weekStartsOn,
  })
  const [getChannelInfo, { data, error }] = useLazyQuery(GetChannelInfo)
  const organizationId = useOrganizationId()

  const isSingleChannel = selectedChannelIds?.length === 1
  const selectedChannelId = isSingleChannel ? selectedChannelIds[0] : undefined
  const { data: postingGoalsData, loading: postingGoalsLoading } = useQuery(
    GetCalendarPostingGoals,
    {
      variables: { organizationId: organizationId ?? '' },
      skip:
        !organizationId ||
        !showTimeSlots ||
        !enableMultiChannelSlots ||
        isSingleChannel ||
        channels.length === 0,
    },
  )
  const areSlotsEnabled = areCalendarSlotsEnabled({
    isSingleChannel,
    showTimeSlots,
    showMultiChannelSlots: enableMultiChannelSlots,
  })

  useEffect(() => {
    if (!showTimeSlots || !organizationId || !selectedChannelId) return

    void getChannelInfo({
      variables: { channelId: selectedChannelId },
    })
  }, [showTimeSlots, organizationId, selectedChannelId, getChannelInfo])

  return useMemo(() => {
    if (!areSlotsEnabled || !organizationId) return []

    if (isSingleChannel) {
      if (!data?.channel) return []

      const channel = readFragment(QueueList_Channel, data.channel)
      if (!channel || channel.id !== selectedChannelId || error) return []

      return buildSlotsForChannel({
        channel,
        endDate: endOfDay(endDate).toPlainDateTime().toString(),
      })
    }

    if (!enableMultiChannelSlots) return []

    if (channels.length === 0) {
      return buildPreviewCalendarSlots({
        onboardingServices,
        timezone,
        weekStartsOn,
        repeatEachWeek: viewMode !== 'three-day',
        startTimestamp: startDate.toInstant().epochMilliseconds,
        endDate: endOfDay(endDate).toInstant().toString(),
      })
    }

    if (postingGoalsLoading) return []

    const goalsByChannelId = new Map<
      string,
      CalendarSlotChannel['postingGoal']
    >()
    for (const channelGoal of postingGoalsData?.organizationPostingGoalsSummary
      .channelGoals ?? []) {
      goalsByChannelId.set(channelGoal.channel.id, channelGoal.postingGoal)
    }
    const channelsWithGoals = channels.map((channel) => ({
      ...channel,
      postingGoal: goalsByChannelId.get(channel.id),
    }))

    return buildMultiChannelCalendarSlots({
      channels: channelsWithGoals,
      timezone,
      weekStartsOn,
      repeatEachWeek: viewMode !== 'three-day',
      startTimestamp: startDate.toInstant().epochMilliseconds,
      endDate: endOfDay(endDate).toInstant().toString(),
    })
  }, [
    channels,
    areSlotsEnabled,
    data?.channel,
    enableMultiChannelSlots,
    endDate,
    error,
    isSingleChannel,
    organizationId,
    onboardingServices,
    postingGoalsData?.organizationPostingGoalsSummary.channelGoals,
    postingGoalsLoading,
    selectedChannelId,
    startDate,
    timezone,
    viewMode,
    weekStartsOn,
  ])
}
